import { useMutation } from '@tanstack/react-query';
import { usePageTitle } from '@/shared/hooks/usePageTitle';
import { Navigate } from 'react-router-dom';

import { useAuth } from '@/app/providers/auth/authContext';
import { routePaths } from '@/app/router/routePaths';
import { isRegistrationOpen, type TeamProfile } from '@/core/schemas';
import { activeTournamentId, useTournament } from '@/features/tournament/useTournament';
import { registerTeam } from '@/infrastructure/repositories/teamRepository';
import { Alert } from '@/shared/ui/Alert';
import { LoadingScreen } from '@/shared/ui/LoadingScreen';
import { PageState } from '@/shared/ui/PageState';

import { TeamForm } from './TeamForm';
import { describeError } from './describeError';
import { useMyTeam } from './useMyTeam';

export function TeamRegistrationPage() {
  usePageTitle('Team anmelden');
  const { session } = useAuth();
  const tournament = useTournament();
  const myTeam = useMyTeam();

  const registration = useMutation({
    mutationFn: (profile: TeamProfile) => {
      if (session.status !== 'authenticated') {
        throw new Error('Nicht angemeldet');
      }
      return registerTeam({
        tournamentId: activeTournamentId,
        profile,
      });
    },
  });

  if (tournament.isLoading || myTeam.isLoading) {
    return <LoadingScreen label="Turnierdaten werden geladen …" />;
  }

  if (tournament.error !== null) {
    return <PageState title="Turnier nicht verfügbar" description={tournament.error.message} />;
  }

  if (tournament.data === undefined) {
    return <LoadingScreen />;
  }

  // Already registered — the captain belongs on their team page.
  if (myTeam.data !== null && myTeam.data !== undefined) {
    return <Navigate to={routePaths.team.profile} replace />;
  }

  const { settings } = tournament.data;

  // Same question the `registerTeam` function asks — the form must not offer
  // what the server will refuse.
  if (!isRegistrationOpen(tournament.data)) {
    return (
      <PageState
        title="Anmeldung geschlossen"
        description="Für dieses Turnier können derzeit keine Teams angemeldet werden. Bitte an die Turnierleitung wenden."
      />
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-8">
      <header className="flex flex-col gap-2">
        <p className="text-brand text-xs font-semibold tracking-widest uppercase">
          {settings.general.name}
        </p>
        <h1 className="text-2xl font-bold tracking-tight">Team anmelden</h1>
        <p className="text-fg-muted text-sm">
          Einmalig auszufüllen. Die Angaben lassen sich bis zum Turnierstart jederzeit ändern.
        </p>
      </header>

      {registration.isSuccess && <Alert tone="success">Das Team wurde angemeldet.</Alert>}

      <TeamForm
        songMaxDurationSeconds={settings.registration.songMaxDurationSeconds}
        submitLabel="Team anmelden"
        isSubmitting={registration.isPending}
        errorMessage={describeError(registration.error)}
        onSubmit={(profile) => {
          registration.mutate(profile);
        }}
      />
    </div>
  );
}
